import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ComponentPrototype } from '../../shared';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { NavigationStart, Router } from '@angular/router';

const MODAL_OPTIONS: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  container: 'body',
  windowClass: 'fade in',
  backdropClass: 'fade in',
};

const EVENT_RETURN_VALUE = 'PREVENT';

@Component({
  selector: 'app-exit-trap',
  template: `
    <ng-template #exitTrapTpl>
      <div class="exit-trap">
        <div class="exit-trap__header">
          <h3 class="exit-trap__header-label">
            <i class="fa fa-exclamation-triangle"></i>
            <span>&nbsp;{{ title }}</span>
          </h3>
          <i class="fa fa-times exit-trap__close" (click)="!isSaving && close()" *ngIf="!isSaving"></i>
        </div>
        <div class="exit-trap__body">
          <ng-content></ng-content>
        </div>
        <div class="exit-trap__footer">
          <button class="btn btn-danger" *ngIf="debugMode" (click)="testClick.emit()">CLICK ME</button>
          <button class="btn btn-default" [disabled]="isSaving" (click)="!isSaving && close()">STAY HERE</button>
          <button class="btn btn-default" [disabled]="isSaving" (click)="!isSaving && cancelChanges.emit()">
            <!--          <button class="btn btn-default" [disabled]="true">-->
            LEAVE WITHOUT SAVING
          </button>
          <button class="btn btn-primary" [disabled]="isSaving" (click)="!isSaving && submitChanges.emit()">
            <span>SAVE & EXIT</span>
            <i class="fa fa-spinner fa-pulse" *ngIf="isSaving"></i>
          </button>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./exit-trap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExitTrapComponent extends ComponentPrototype implements OnInit {
  @Input() public title: string = '';

  @Input() public isSaving: boolean = false;

  @Input() public hasChanges: boolean = false;

  @Input() public debugMode: boolean = false;

  @Output() public stateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() public routeChange: EventEmitter<string> = new EventEmitter<string>();

  @Output() public cancelChanges: EventEmitter<void> = new EventEmitter<void>();

  @Output() public submitChanges: EventEmitter<void> = new EventEmitter<void>();

  @Output() public testClick: EventEmitter<void> = new EventEmitter<void>();

  @Output() public componentReady: EventEmitter<ExitTrapComponent> = new EventEmitter<ExitTrapComponent>();

  @ViewChild('exitTrapTpl') public exitTrapTpl: HTMLElement;

  @HostListener('window:beforeunload', ['$event']) public onReloadPage = (event: any): any => {
    this.hasChanges && (event.returnValue = EVENT_RETURN_VALUE);
  };

  private readonly isOpened$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public close = (): this => {
    this.modalService.dismissAll();
    this.isOpened$.next(false);

    return this;
  };

  public open = (): this => {
    this.modalService.open(this.exitTrapTpl, MODAL_OPTIONS);
    this.isOpened$.next(true);

    return this;
  };

  public get isOpened(): boolean {
    return this.isOpened$.value;
  }

  constructor(public modalService: NgbModal, public router: Router) {
    super();
  }

  ngOnInit(): void {
    const { isOpened$, stateChange, routeChange, router, componentReady, registerSubscriptions } = this;

    registerSubscriptions(
      isOpened$
        .pipe(
          distinctUntilChanged(),
          tap(isOpened => stateChange.emit(isOpened))
        )
        .subscribe(),

      router.events
        .pipe(
          filter(e => e instanceof NavigationStart),
          tap((event: NavigationStart) => {
            this.hasChanges &&
              router.navigateByUrl(router.routerState.snapshot.url, { skipLocationChange: true }).then(() => {
                routeChange.emit(event.url);
              });
          })
        )
        .subscribe()
    );

    componentReady.emit(this);
  }
}
